const API_URL = window.location.protocol + '//' + window.location.host + '/api';

// Bestehende Interfaces
interface ApiResponse {
  success: boolean;
  message: string;
  data?: any;
}

interface DbProduct {
  id: number;
  user_id: number;
  title: string;
  description?: string;
  price: number;
  category: string;
  product_type?: string;
  club: string;
  size: string;
  image_url: string;
  images?: string[];
  created_at: string;
  is_archived: boolean;
  seller_name?: string;
  seller_club?: string;
  views?: number;
}

interface Product extends DbProduct {
  // Keine zusätzlichen Felder nötig
}

interface RegisterData {
  name: string;
  email: string;
  password: string;
  club: string;
}

interface LoginData {
  email: string;
  password: string;
}

interface UpdateProfileData {
  name: string;
  club: string;
}

interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
}

interface CreateProductData {
  title: string;
  description?: string;
  price: number;
  category: string;
  product_type?: string;
  size: string;
  image_url?: string;
  additional_images?: string[];
}

interface UpdateProductData extends CreateProductData {
  // Gleiche Felder wie CreateProductData
}

// Neue Message Interfaces
interface Message {
  id: number;
  sender_id: number;
  receiver_id: number;
  product_id: number;
  message: string;
  is_read: boolean;
  created_at: string;
  sender_name?: string;
  receiver_name?: string;
}

interface SendMessageData {
  receiver_id: number;
  product_id: number;
  message: string;
}

// Konversationsübersicht Interface
interface Conversation {
  product_id: number;
  product_title: string;
  product_image: string;
  other_user_name: string;
  other_user_id: number;
  last_message_time: string;
  unread_count: number;
}

// Admin Interfaces
interface AdminStats {
  totalUsers: number;
  totalProducts: number;
  activeProducts: number;
  totalMessages: number;
  newUsersToday: number;
  newProductsToday: number;
}

interface AdminUser {
  id: number;
  name: string;
  email: string;
  club: string;
  created_at: string;
  status: string;
}

// Neue Interfaces für die erweiterten Funktionen
interface ActiveUser {
  id: number;
  name: string;
  email: string;
  last_activity: string; // ISO String Format
}

interface DailyVisitor {
  date: string;
  visitors: number;
}

// Bestehende Funktionen
export const markProductSold = async (productId: number): Promise<ApiResponse> => {
  try {
    const response = await fetch(`${API_URL}/auth/mark-sold.php`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ product_id: productId }),
    });
    return await response.json();
  } catch (error) {
    return { success: false, message: 'Fehler beim Markieren als verkauft' };
  }
};

export const deleteProduct = async (productId: number): Promise<ApiResponse> => {
  try {
    const response = await fetch(`${API_URL}/auth/delete-product.php`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ product_id: productId }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Fehler beim Löschen des Produkts',
    };
  }
};

export const getProductImages = async (productId: number): Promise<ApiResponse> => {
  try {
    const response = await fetch(`${API_URL}/products.php?id=${productId}&images=true`, {
      credentials: 'include'
    });
    return await response.json();
  } catch (error) {
    return {
      success: false,
      message: 'Fehler beim Laden der Produktbilder.',
    };
  }
};

export const updateProduct = async (productId: number, data: UpdateProductData): Promise<ApiResponse> => {
  try {
    const requestData = {
      product_id: productId,
      ...data
    };

    const response = await fetch(`${API_URL}/auth/update-product.php`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestData)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Fehler beim Aktualisieren des Produkts',
    };
  }
};

export const uploadImages = async (files: File[], productId?: number): Promise<ApiResponse> => {
  try {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('images[]', file);
    });

    if (productId) {
      formData.append('product_id', productId.toString());
    }

    const response = await fetch(`${API_URL}/auth/upload.php`, {
      method: 'POST',
      credentials: 'include',
      body: formData
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    return {
      success: false,
      message: 'Fehler beim Hochladen der Bilder.',
    };
  }
};

export const register = async (userData: RegisterData): Promise<ApiResponse> => {
  try {
    const response = await fetch(`${API_URL}/auth/register.php`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData),
    });
    return await response.json();
  } catch (error) {
    return {
      success: false,
      message: 'Ein Fehler ist aufgetreten bei der Registrierung.',
    };
  }
};

export const login = async (loginData: LoginData): Promise<ApiResponse> => {
  try {
    const response = await fetch(`${API_URL}/auth/login.php`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(loginData),
    });
    return await response.json();
  } catch (error) {
    return {
      success: false,
      message: 'Ein Fehler ist aufgetreten beim Login.',
    };
  }
};

export const updateProfile = async (data: UpdateProfileData): Promise<ApiResponse> => {
  try {
    const response = await fetch(`${API_URL}/auth/update-profile.php`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return await response.json();
  } catch (error) {
    return {
      success: false,
      message: 'Ein Fehler ist aufgetreten beim Aktualisieren des Profils.',
    };
  }
};

export const changePassword = async (data: ChangePasswordData): Promise<ApiResponse> => {
  try {
    const response = await fetch(`${API_URL}/auth/change-password.php`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return await response.json();
  } catch (error) {
    return {
      success: false,
      message: 'Ein Fehler ist aufgetreten beim Ändern des Passworts.',
    };
  }
};

export const createProduct = async (productData: CreateProductData): Promise<ApiResponse> => {
  try {
    const response = await fetch(`${API_URL}/auth/create-product.php`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(productData),
    });
    return await response.json();
  } catch (error) {
    return {
      success: false,
      message: 'Ein Fehler ist aufgetreten beim Erstellen des Produkts.',
    };
  }
};

export const getProducts = async (): Promise<ApiResponse> => {
  try {
    const response = await fetch(`${API_URL}/products.php`, {
      credentials: 'include'
    });
    return await response.json();
  } catch (error) {
    return {
      success: false,
      message: 'Fehler beim Laden der Produkte.',
    };
  }
};

export const getProduct = async (id: string): Promise<ApiResponse> => {
  try {
    const response = await fetch(`${API_URL}/products.php?id=${id}`, {
      credentials: 'include'
    });
    return await response.json();
  } catch (error) {
    return {
      success: false,
      message: 'Fehler beim Laden des Produkts.',
    };
  }
};

export const getMyProducts = async (): Promise<ApiResponse> => {
  try {
    const response = await fetch(`${API_URL}/auth/my-products.php`, {
      credentials: 'include'
    });
    return await response.json();
  } catch (error) {
    return {
      success: false,
      message: 'Fehler beim Laden deiner Produkte.',
    };
  }
};

// Neue Nachrichten-Funktionen
export const sendMessage = async (messageData: SendMessageData): Promise<ApiResponse> => {
  try {
    const response = await fetch(`${API_URL}/auth/messages.php`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(messageData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    return {
      success: false,
      message: 'Fehler beim Senden der Nachricht.',
    };
  }
};

export const getMessages = async (productId: number, otherUserId?: number): Promise<ApiResponse> => {
  try {
    let url;
    if (productId > 0 && otherUserId) {
      url = `${API_URL}/auth/messages.php?product_id=${productId}&user_id=${otherUserId}`;
    } else if (productId > 0) {
      url = `${API_URL}/auth/messages.php?product_id=${productId}`;
    } else {
      url = `${API_URL}/auth/messages.php`;
    }

    const response = await fetch(url, {
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    return {
      success: false,
      message: 'Fehler beim Laden der Nachrichten.',
    };
  }
};

export const markMessagesAsRead = async (productId: number, otherUserId?: number): Promise<ApiResponse> => {
  try {
    const data: any = { product_id: productId };
    if (otherUserId) {
      data.user_id = otherUserId;
    }

    const response = await fetch(`${API_URL}/auth/messages.php`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    return {
      success: false,
      message: 'Fehler beim Markieren der Nachrichten als gelesen.',
    };
  }
};

export const deleteConversation = async (productId: number, otherUserId?: number): Promise<ApiResponse> => {
  try {
    const data: any = { product_id: productId };
    if (otherUserId) {
      data.user_id = otherUserId;
    }

    const response = await fetch(`${API_URL}/auth/messages.php`, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`HTTP-Fehler! Status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    return {
      success: false,
      message: 'Fehler beim Löschen der Konversation.',
    };
  }
};

// Admin Funktionen
export const fetchAdminStats = async (): Promise<AdminStats> => {
  try {
    const response = await fetch(`${API_URL}/auth/admin.php?action=stats`, {
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result.data;
  } catch (error) {
    throw error;
  }
};

export const fetchRecentProducts = async (): Promise<DbProduct[]> => {
  try {
    const response = await fetch(`${API_URL}/auth/admin.php?action=recent-products`, {
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result.data;
  } catch (error) {
    throw error;
  }
};

export const fetchRecentUsers = async (): Promise<AdminUser[]> => {
  try {
    const response = await fetch(`${API_URL}/auth/admin.php?action=recent-users`, {
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result.data;
  } catch (error) {
    throw error;
  }
};

export const banUser = async (userId: number): Promise<ApiResponse> => {
  try {
    const response = await fetch(`${API_URL}/auth/admin.php?action=ban-user`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ user_id: userId })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
};

export const unbanUser = async (userId: number): Promise<ApiResponse> => {
  try {
    const response = await fetch(`${API_URL}/auth/admin.php?action=unban-user`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ user_id: userId })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
};

export const saveAdminSettings = async (settings: any): Promise<ApiResponse> => {
  try {
    const response = await fetch(`${API_URL}/auth/admin.php?action=settings`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(settings)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    return {
      success: false,
      message: 'Fehler beim Speichern der Einstellungen.'
    };
  }
};

export const fetchActiveUsers = async (): Promise<ActiveUser[]> => {
  try {
    const response = await fetch(`${API_URL}/auth/admin.php?action=active-users`, {
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result.data || [];
  } catch (error) {
    throw error;
  }
};

export const fetchVisitorStats = async (): Promise<DailyVisitor[]> => {
  try {
    const response = await fetch(`${API_URL}/auth/admin.php?action=visitor-stats`, {
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result.data || [];
  } catch (error) {
    throw error;
  }
};

// Bewertungs-Funktionen
export const getSellerRatings = async (sellerId: number): Promise<ApiResponse> => {
  try {
    const response = await fetch(`${API_URL}/ratings.php?seller_id=${sellerId}`, {
      credentials: 'include'
    });
    return await response.json();
  } catch (error) {
    return { success: false, message: 'Fehler beim Laden der Bewertungen.' };
  }
};

export const getMyRatingForSeller = async (sellerId: number): Promise<ApiResponse> => {
  try {
    const response = await fetch(`${API_URL}/auth/ratings.php?seller_id=${sellerId}`, {
      credentials: 'include'
    });
    return await response.json();
  } catch (error) {
    return { success: false, message: 'Fehler beim Laden der Bewertung.' };
  }
};

export const submitRating = async (sellerId: number, rating: number, comment: string): Promise<ApiResponse> => {
  try {
    const response = await fetch(`${API_URL}/auth/ratings.php`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ seller_id: sellerId, rating, comment }),
    });
    return await response.json();
  } catch (error) {
    return { success: false, message: 'Fehler beim Speichern der Bewertung.' };
  }
};

// Favoriten Funktionen
export const getFavorites = async (): Promise<ApiResponse> => {
  try {
    const response = await fetch(`${API_URL}/auth/favorites.php`, {
      credentials: 'include'
    });
    return await response.json();
  } catch (error) {
    return { success: false, message: 'Fehler beim Laden der Merkliste.' };
  }
};

export const toggleFavorite = async (productId: number): Promise<ApiResponse> => {
  try {
    const response = await fetch(`${API_URL}/auth/favorites.php`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ product_id: productId }),
    });
    return await response.json();
  } catch (error) {
    return { success: false, message: 'Fehler beim Aktualisieren der Merkliste.' };
  }
};

// Exportiere Typen
export type {
  DbProduct,
  Product,
  ApiResponse,
  RegisterData,
  LoginData,
  UpdateProfileData,
  ChangePasswordData,
  CreateProductData,
  UpdateProductData,
  Message,
  SendMessageData,
  Conversation,
  AdminStats,
  AdminUser,
  ActiveUser,
  DailyVisitor
};
